import React, { useState } from 'react';
import {Tooltip, IconButton, Box, Typography, Divider} from '@mui/material';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import moment from 'moment';
import {Failures} from "@features/home/types";
import {HealthCheckStatus} from "@features/home-health/types";

interface HealthTooltipProps {
  failureEvents?: Failures;
  successful: HealthCheckStatus;
  failed?: HealthCheckStatus;
  unavailable?: string[];
  gateway: moment.Moment | null;
  onClick: () => void;
}

const formatCheckType = (checkType: string): string => {
  return checkType
    .toLowerCase()
    .split('_')
    .map((word, index) => index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word)
    .join(' ');
};

const _formatTimestamp = (timestamp: moment.Moment | null): string => {
  if (timestamp === null) {
    return "Never";
  }
  const now = moment();
  const diffInHours = now.diff(timestamp, 'hours');

  if (diffInHours < 24) {
    const hours = Math.floor(diffInHours);
    const minutes = Math.abs(timestamp.diff(now, 'minutes') % 60);
    if (hours === 0) {
      return `${minutes}m ago`;
    }
    return `${hours}h ${minutes}m ago`;
  }

  const days = Math.floor(diffInHours / 24);
  return `${days}d ago`;
};

const formatTimestamp = (timestamp: moment.Moment | null): string => {
  if (timestamp === null) {
    return "Never";
  }
  return timestamp.format('MMM D h:mma').toLowerCase();
};

const TooltipContent: React.FC<{
  failureEvents?: Failures;  // Made optional
  successful: HealthCheckStatus;
  failed?: HealthCheckStatus;
  unavailable?: string[];
  gateway: moment.Moment | null;
  isExpanded: boolean;
  onToggleExpand: (e: React.MouseEvent) => void;
}> = ({ failureEvents = {}, successful, failed = {}, unavailable = [], gateway, isExpanded, onToggleExpand }) => (
  <Box component="div" sx={{ position: 'relative', minWidth: '290px' }}>
    <IconButton
      onClick={onToggleExpand}
      sx={{
        position: 'absolute',
        padding: 0.4,
        top: -4,
        right: -4,
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.4)',
        }
      }}
    >
      {isExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
    </IconButton>

    <Box component="div" sx={{ pr: 4 }}>
      {Object.keys(failureEvents).length > 0 && (
        <>
          <Typography variant="body1">Failure events: {Object.keys(failureEvents).length}</Typography>
          {isExpanded && Object.entries(failureEvents).map(([key, count]) => (
            <div key={key} style={{ paddingLeft: '12px' }}>{key}: {count}</div>
          ))}
          <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.3)" }}/>
        </>
      )}

      {failed && Object.keys(failed).length > 0 && (
        <>
          <Typography variant="body1">Failed health checks: {Object.keys(failed).length}</Typography>
          {isExpanded && Object.entries(failed).map(([type, timestamp]) => (
            <Typography variant="subtitle2" key={type} sx={{ paddingLeft: '12px' }}>
              {formatCheckType(type)} (last checked {formatTimestamp(timestamp)})
            </Typography>
          ))}
          <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.3)" }}/>
        </>
      )}

      {unavailable && unavailable.length > 0 && (
        <>
          <Typography variant="body1">
            Health checks not performed: {unavailable.length}
          </Typography>
          {isExpanded && (
            <Typography variant="subtitle2" sx={{ paddingLeft: '12px' }}>
              {unavailable.map(formatCheckType).join(', ')}
            </Typography>
          )}
          <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.3)" }}/>
        </>
      )}

      <Typography variant="body1">Successful health checks: {Object.keys(successful).length}</Typography>
      {isExpanded && Object.entries(successful).map(([type, timestamp]) => (
        <Typography variant="subtitle2" key={type} style={{ paddingLeft: '12px' }}>
          {formatCheckType(type)} (last checked {formatTimestamp(timestamp)})
        </Typography>
      ))}
      <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.3)" }}/>
      <Typography variant="subtitle2" sx={{ marginTop: '8px' }}>
        Last update from Gateway: {formatTimestamp(gateway)}
      </Typography>
    </Box>
  </Box>
);

export const HealthTooltip: React.FC<HealthTooltipProps> = ({
  failureEvents = {},
  successful,
  failed = {},
  unavailable = [],
  gateway,
  onClick,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const getIcon = () => {
    // Check for no gateway update or update older than 24 hours
    if (!gateway || moment().diff(gateway, 'hours') >= 24) {
      return <ErrorIcon color="error" />;
    }

    // Default case - everything is good
    return <CheckCircleIcon color="success" />;
  };

  return (
    <Tooltip
      title={
        <TooltipContent
          failureEvents={failureEvents}
          successful={successful}
          failed={failed}
          unavailable={unavailable}
          gateway={gateway}
          isExpanded={isExpanded}
          onToggleExpand={handleToggleExpand}
        />
      }
      placement="top"
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.75)',
            color: 'rgba(255, 255, 255, 1)',
            padding: '12px',
            '& .MuiTooltip-arrow': {
              color: 'rgba(0, 0, 0, 0.75)',
            },
            '& div': {
              marginBottom: '4px',
            },
            '& div:last-child': {
              marginBottom: 0,
            },
          },
        },
      }}
      PopperProps={{
        modifiers: [{
          name: 'offset',
          options: {
            offset: [0, -10],
          },
        }],
      }}
    >
      <IconButton onClick={onClick}>
        {getIcon()}
      </IconButton>
    </Tooltip>
  );
};